/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { graphql, HttpResponse } from 'msw';

import {
  UserProfileForSettingDocument,
  type UserProfileForSettingQuery,
  UserIdentityTypeDocument,
  UserIdDocument,
  ClientUserIdentityType,
  type UserIdentityTypeQuery,
  type HuggingFaceIntegrationStatusQuery,
  HuggingFaceIntegrationStatusDocument,
  type WandbIntegrationStatusQuery,
  WandbIntegrationStatusDocument,
  TeamPaymentBillingAmountAndDateDocument,
  type TeamPaymentBillingAmountAndDateQuery,
  UserDefaultTeamIdDocument,
  UserInfoWithEmailDocument,
  ClientUserCreateAvatarUploadUrlDocument,
} from '@fai/friendli-suite/generated/client/graphql';

import { mockUser } from './validation';

const useUserIdQuery = graphql.query(UserIdDocument, () =>
  HttpResponse.json({
    data: {
      clientSession: {
        user: {
          id: mockUser.id,
        },
      },
    },
  })
);

const useUserDefaultTeamIdQuery = graphql.query(UserDefaultTeamIdDocument, () =>
  HttpResponse.json({
    data: {
      clientSession: {
        user: {
          id: mockUser.id,
          teamDefault: {
            team: {
              id: mockUser.defaultTeam.id,
            },
          },
        },
      },
    },
  })
);

const useUserInfoWithEmailQuery = graphql.query(UserInfoWithEmailDocument, () =>
  HttpResponse.json({
    data: {
      clientSession: {
        user: {
          id: mockUser.id,
          name: mockUser.name,
          avatarUrl: mockUser.avatarUrl,
          email: mockUser.email,
        },
      },
    },
  })
);

const useCreateAvatarUploadUrlMutation = graphql.mutation(
  ClientUserCreateAvatarUploadUrlDocument,
  () =>
    HttpResponse.json({
      data: {
        clientUserCreateAvatarUploadUrl: {
          __typename: mockUser.avatarUpload.__typename,
          uploadInfo: mockUser.avatarUpload.uploadInfo,
        },
      },
    })
);

export function customUserIdentityTypeQuery(identityType: ClientUserIdentityType) {
  return graphql.query<UserIdentityTypeQuery>(UserIdentityTypeDocument, () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            identityType,
          },
        },
      },
    })
  );
}

const useUserProfileForSettingQuery = graphql.query<UserProfileForSettingQuery>(
  UserProfileForSettingDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            name: mockUser.name,
            email: mockUser.email,
          },
        },
      },
    })
);

const useHuggingFaceIntegrationStatusQuery = graphql.query<HuggingFaceIntegrationStatusQuery>(
  HuggingFaceIntegrationStatusDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            integration: {
              huggingface: mockUser.integrations.huggingface,
            },
          },
        },
      },
    })
);

const useWandbIntegrationStatusQuery = graphql.query<WandbIntegrationStatusQuery>(
  WandbIntegrationStatusDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            integration: {
              wandb: mockUser.integrations.wandb,
            },
          },
        },
      },
    })
);

const usePaymentAmountAndDateQuery = graphql.query<TeamPaymentBillingAmountAndDateQuery>(
  TeamPaymentBillingAmountAndDateDocument,
  () =>
    HttpResponse.json({
      data: {
        clientTeam: {
          id: mockUser.defaultTeam.id,
          billing: {
            monthlyPayment: mockUser.defaultTeam.billing.monthlyPayment,
            nextPaymentDate: mockUser.defaultTeam.billing.nextPaymentDate,
          },
        },
      },
    })
);

const accountHandlers = [
  useUserIdQuery,
  useUserDefaultTeamIdQuery,
  useUserInfoWithEmailQuery,
  useUserProfileForSettingQuery,
  useHuggingFaceIntegrationStatusQuery,
  useWandbIntegrationStatusQuery,
  usePaymentAmountAndDateQuery,
  useCreateAvatarUploadUrlMutation,
  customUserIdentityTypeQuery(ClientUserIdentityType.Emailpassword),
];

export default accountHandlers;
